import { Injectable, OnDestroy } from '@angular/core';
import { Params, Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { OperationObserverService } from '@x/common/operation';
import { ResponsiveService } from '@x/common/responsive';
import { ChannelState } from '@x/ecommerce-shop/app/channel/state';
import { AddressService } from '@x/ecommerce-shop/app/core/address/services/address.service';
import {
  OrderAddCartItem,
  OrderUpdateCartAddress,
  OrderUpdateCartCollectionPoint,
} from '@x/ecommerce-shop/app/core/order/state/shop-order.actions';
import { ShopOrderState } from '@x/ecommerce-shop/app/core/order/state/shop-order.state';
import {
  RegionCloseOverlay,
  RegionUpdateCoordinates,
} from '@x/ecommerce-shop/app/core/region/state/region.actions';
import {
  IProductContext,
  IRegionStateModel,
  RegionState,
} from '@x/ecommerce-shop/app/core/region/state/region.state';
import { ShopStorage } from '@x/ecommerce-shop/app/core/shop-storage/shop-storage.service';
import {
  IShopAddressInput,
  IShopCollectionPoint,
  IShopGeoRegionDetail,
} from '@x/ecommerce/shop-client';
import { AddressAssignment, PointInput } from '@x/schemas/ecommerce';
import { BehaviorSubject, Observable, combineLatest, finalize, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable()
export class RegionOverlayService implements OnDestroy {
  private regionState$: Observable<IRegionStateModel> = this.store.select(RegionState);

  private operationQ$ = this.operationService.createQueue();
  operationQState$ = this.operationQ$.observe();

  showMap$ = new BehaviorSubject(false);

  disableConfirm$ = combineLatest([
    this.regionState$,
    this.store.select(ShopOrderState.orderShippingAddress),
  ]).pipe(
    map(([regionState, orderAddress]) => {
      if (!this.regionInActiveChannel(regionState)) return true;

      if (regionState?.addressInput?.coordinates && orderAddress?.coordinates) {
        return this.coordinatesMatch(
          regionState.addressInput.coordinates,
          orderAddress.coordinates,
        );
      }

      return false;
    }),
  );

  constructor(
    private store: Store,
    private operationService: OperationObserverService,
    private router: Router,
    private addressService: AddressService,
    private responsiveService: ResponsiveService,
    private shopStorage: ShopStorage,
  ) {
    this.operationQState$.subscribe();
  }

  ngOnDestroy(): void {
    this.operationQ$.destroy();
  }

  handleOutSideClick($event: MouseEvent) {
    const ignoreListByClassName = ['mat-option-text', 'mat-option'];
    const target = $event.target as Element;

    const ignoredItem = Object.values(target.classList).find((name) =>
      ignoreListByClassName.includes(name),
    );
    if (ignoredItem) return;

    if (this.responsiveService.currentOrientation === 'PORTRAIT') return;

    this.closeOverlay();
  }

  handleOverlayKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeOverlay();
    }
  }

  addToOperationQ(operation: Observable<any>) {
    this.operationQ$.queue(operation);
  }

  regionInActiveChannel(state: IRegionStateModel): boolean {
    const { region, channelRegions } = state;

    if (!region) return false;
    return channelRegions.map((r) => r.id).includes(region.id) ?? false;
  }

  regionSupportedInChannels(region: IShopGeoRegionDetail | null): boolean {
    if (!region) return false;

    return (
      this.store.selectSnapshot(ChannelState.channels).find((channel) => {
        return channel.regions.map((r) => r.id).includes(region.id);
      }) !== undefined
    );
  }

  isProductInSelectedRegion(state: IRegionStateModel): boolean {
    if (state.region && state.productContext) {
      const { variantAvailableRegions } = state.productContext;
      return variantAvailableRegions.map((r) => r.id).includes(state.region.id);
    }

    return false;
  }

  confirm(state: IRegionStateModel) {
    if (!this.regionInActiveChannel(state)) return;

    const { addressInput } = state;
    if (!addressInput) return;

    if (this.showMap$.value) this.showMap$.next(false);

    this.assignAddressToOrder(addressInput);
  }

  private assignAddressToOrder(addressInput: IShopAddressInput) {
    this.operationQ$.queue(
      this.store
        .dispatch(
          new OrderUpdateCartAddress({
            address: addressInput,
            assignment: AddressAssignment.Shipping,
          }),
        )
        .pipe(finalize(() => this.closeOverlay())),
    );
  }

  closeOverlay() {
    this.showMap$.next(false);
    this.store.dispatch(new RegionCloseOverlay());
  }

  async login(productContext?: IProductContext | null) {
    let queryParams: Params = {
      regionOverlay: 'open',
      redirect: decodeURIComponent(this.router.url).split('?')[0],
      ...(productContext && {
        prodId: productContext.id,
        variantId: productContext.variantId,
        quantity: productContext.quantity,
      }),
    };

    await this.router.navigate(['/login'], { queryParams });
  }

  async gotoChannel(state: IRegionStateModel) {
    const { region } = state;
    if (!region?.channelCode) return;

    const { channelCode } = region;
    this.shopStorage.channel = channelCode;
    let queryParams: Params = { regionOverlay: 'open' };
    await this.router.navigate([`/${channelCode}`], { queryParams });
  }

  addToCart(state: IRegionStateModel) {
    const { productContext, addressInput } = state;
    if (!productContext || !addressInput) return;

    const { quantity, variantId } = productContext;

    this.toggleShowMap();

    this.operationQ$.queue(
      this.store
        .dispatch(
          new OrderUpdateCartAddress({
            address: addressInput,
            assignment: AddressAssignment.Shipping,
          }),
        )
        .pipe(
          switchMap(() =>
            this.store.dispatch(new OrderAddCartItem({ quantity, productVariantId: variantId })),
          ),
          finalize(() => this.closeOverlay()),
        ),
    );
  }

  toggleShowMap() {
    if (this.responsiveService.currentOrientation === 'PORTRAIT') {
      this.showMap$.next(!this.showMap$.value);
    }
  }

  collect(point: IShopCollectionPoint) {
    const address = this.addressService.getAddressInputFromShopAddress(point.address);
    const region = point.address.region;

    if (!region || !address) return;

    this.operationQ$.queue(
      this.store
        .dispatch(new OrderUpdateCartCollectionPoint({ collectionPointId: point.id }))
        .pipe(finalize(() => this.closeOverlay())),
    );
  }

  updateCoordinates(coordinates: PointInput) {
    this.store.dispatch(new RegionUpdateCoordinates(coordinates));
  }

  private coordinatesMatch(coordinates1: PointInput, coordinates2: PointInput): boolean {
    return coordinates1.lat === coordinates2.lat && coordinates1.lng === coordinates2.lng;
  }
}
